import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMin, fmtLessonWhen } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, PencilLine, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const toHM = (m) => `${Math.floor(m / 60)}:${String(Math.round(m % 60)).padStart(2, "0")}`;
const parseHM = (v) => {
  const m = /^(\d{1,3})(?::([0-5]\d))?$/.exec((v || "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2] || 0) : null;
};

function LessonRow({ lr, value, onChange, onReset }) {
  const a = lr.attendance;
  const bad = value !== undefined && (parseHM(value) === null || parseHM(value) > lr.lesson.duration_minutes);
  return (
    <tr className="border-t border-slate-100">
      <td className="py-2 pr-3 text-sm whitespace-nowrap">L{lr.lesson.number} · {fmtLessonWhen(lr.lesson)}</td>
      <td className="py-2 pr-3 text-sm tabular text-slate-500">{fmtMin(lr.lesson.duration_minutes)}</td>
      <td className="py-2 pr-3">
        <div className="flex items-center gap-2">
          <Input value={value ?? toHM(a.recognized_minutes)} onChange={(e) => onChange(e.target.value)}
            className={`w-24 h-8 tabular ${bad ? "border-rose-400" : ""}`} placeholder="h:mm" data-testid={`edit-min-${lr.lesson.id}`} />
          {a.manual_minutes != null && <span className="text-[11px] text-amber-700 flex items-center gap-1"><PencilLine size={12} /> manuale</span>}
        </div>
      </td>
      <td className="py-2 text-right">
        {a.manual_minutes != null && (
          <button className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 ml-auto" onClick={onReset} data-testid={`reset-min-${lr.lesson.id}`}>
            <RotateCcw size={12} /> Ripristina CSV
          </button>
        )}
      </td>
    </tr>
  );
}

export default function EditHoursDialog({ row, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/enrollments/${row.enrollment_id}/detail`).then((r) => setData(r.data));
  useEffect(() => { setEdits({}); setReason(""); setData(null); if (row) load(); }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  const lessons = (data?.lessons || []).filter((lr) => lr.lesson.status === "svolta");
  const changed = Object.entries(edits).filter(([lid, v]) => {
    const lr = lessons.find((x) => x.lesson.id === lid);
    return lr && parseHM(v) !== null && parseHM(v) !== Math.round(lr.attendance.recognized_minutes);
  });
  const invalid = Object.entries(edits).some(([lid, v]) => {
    const lr = lessons.find((x) => x.lesson.id === lid);
    return parseHM(v) === null || (lr && parseHM(v) > lr.lesson.duration_minutes);
  });

  const adjust = (lid, value) => api.post(`/enrollments/${row.enrollment_id}/lessons/${lid}/adjust`,
    { field: "minutes", new_value: value, reason: reason.trim() });

  const save = async () => {
    setSaving(true);
    try {
      for (const [lid, v] of changed) await adjust(lid, String(parseHM(v)));
      toast.success("Ore aggiornate e valori ricalcolati");
      onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const reset = async (lid) => {
    if (!reason.trim()) { toast.error("Indica il motivo della modifica"); return; }
    try { await adjust(lid, ""); toast.success("Ripristinato il valore calcolato dal CSV"); setEdits((e) => { const n = { ...e }; delete n[lid]; return n; }); load(); onSaved(false); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="edit-hours-dialog">
        <DialogHeader><DialogTitle>Modifica ore frequentate — {row?.participant.last_name} {row?.participant.first_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Inserisci il tempo presente per ogni lezione svolta (formato ore:minuti). Totale, percentuali, assenze e andamento vengono ricalcolati. Le modifiche manuali restano anche se il CSV viene reimportato.</p>
        {!data ? <div className="py-6 text-center"><Loader2 className="animate-spin inline" /></div> : (
          <div className="max-h-[50vh] overflow-y-auto">
            <table className="w-full">
              <thead><tr className="text-xs text-slate-500 text-left"><th className="pb-1">Lezione</th><th className="pb-1">Durata</th><th className="pb-1">Presenza</th><th /></tr></thead>
              <tbody>
                {lessons.map((lr) => (
                  <LessonRow key={lr.lesson.id} lr={lr} value={edits[lr.lesson.id]}
                    onChange={(v) => setEdits((e) => ({ ...e, [lr.lesson.id]: v }))} onReset={() => reset(lr.lesson.id)} />
                ))}
                {lessons.length === 0 && <tr><td colSpan={4} className="py-4 text-sm text-slate-400">Nessuna lezione svolta.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo della modifica (obbligatorio)" data-testid="edit-hours-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving || invalid || !changed.length || !reason.trim()} className="bg-[#0B192C] hover:bg-[#07111E]" data-testid="edit-hours-save">
            {saving && <Loader2 size={14} className="mr-1 animate-spin" />} Salva {changed.length ? `(${changed.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
